import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { fetchMemberDecisions } from '../memberDecisionsApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';
import './MemberDecisionsPage.css';

export default function MemberDecisionsPage() {
  const { direction } = useLanguage();
  const t = useT();

  const voteLabels = useMemo(() => ({
    approve: { label: t('decisions.voteApprove'), cls: 'dde__vote-tag--approve', iconName: 'check-circle', color: '#02c3a6' },
    reject:  { label: t('decisions.voteReject'),  cls: 'dde__vote-tag--reject',  iconName: 'x-circle',     color: '#e53935' },
    abstain: { label: t('decisions.voteAbstain'), cls: 'dde__vote-tag--abstain', iconName: 'clock',        color: '#78909c' },
  }), [t]);

  const STATUS_LABELS = useMemo(() => ({
    pending:  { label: t('decisions.voteStatusPending'),  cls: 'status-badge--pending',  iconName: 'clock' },
    approved: { label: t('decisions.voteStatusApproved'), cls: 'status-badge--approved', iconName: 'check-circle' },
    rejected: { label: t('decisions.voteStatusRejected'), cls: 'status-badge--rejected', iconName: 'x-circle' },
  }), [t]);

  const MY_VOTE_FILTER = useMemo(() => [
    { key: 'all',     label: t('decisions.allDecisions'),    iconName: 'list',         activeCls: 'status-tab--active-all' },
    { key: 'pending', label: t('decisions.notVotedFilter'),  iconName: 'clock',        activeCls: 'status-tab--active-pending' },
    { key: 'voted',   label: t('decisions.alreadyVoted'),    iconName: 'check-circle', activeCls: 'status-tab--active-approved' },
  ], [t]);

  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab]             = useState('all');
  const [search, setSearch]       = useState('');
  const [filterBranch, setFilterBranch] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const rows = await fetchMemberDecisions();
        if (!cancelled) setDecisions(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branches = [...new Set(decisions.map((d) => d.branch))];

  const filtered = useMemo(() => decisions.filter((d) => {
    const matchSearch  = !search || d.title.includes(search) || d.branch.includes(search);
    const matchBranch = filterBranch === 'all' || d.branch === filterBranch;
    const matchTab =
      tab === 'all'    ? true :
      tab === 'pending' ? !d.myVote :
      tab === 'voted'   ? !!d.myVote : true;
    return matchSearch && matchBranch && matchTab;
  }), [decisions, search, filterBranch, tab]);

  const counts = {
    all:     decisions.length,
    pending: decisions.filter((d) => !d.myVote).length,
    voted:   decisions.filter((d) => !!d.myVote).length,
  };

  const daysLeft = (deadline) => {
    if (!deadline) return null;
    const parts = String(deadline).split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [year, month, day] = parts;
    const endOfDeadlineDay = new Date(year, month - 1, day, 23, 59, 59, 999);
    return Math.ceil((endOfDeadlineDay - new Date()) / 86400000);
  };

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t('decisions.title')}
            </span>
            <span className="list-page-card__sub">{t('decisions.memberSubtitle')}</span>
          </div>
        </header>

        <div className="page__stats">
          <div className="page__stat-chip page__stat-chip--total"><Icon name="decisions" size={13} /> {t('decisions.totalStat')}: {counts.all}</div>
          <div className="page__stat-chip page__stat-chip--new"><Icon name="clock" size={13} /> {t('decisions.notVotedStat')}: {counts.pending}</div>
          <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t('decisions.votedStat')}: {counts.voted}</div>
        </div>

        <div className="status-tabs">
          {MY_VOTE_FILTER.map((row) => (
            <button
              type="button"
              key={row.key}
              className={`status-tab${tab === row.key ? ` ${row.activeCls}` : ''}`}
              onClick={() => setTab(row.key)}
            >
              <Icon name={row.iconName} size={13} style={{ verticalAlign: 'middle', marginLeft: 5 }} />
              {row.label} ({counts[row.key] ?? counts.all})
            </button>
          ))}
        </div>

        <div className="page__filters">
          <div className="page__search">
            <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
            <input
              className="page__search-input"
              placeholder={t('decisions.searchPh')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <span role="button" tabIndex={0} style={{ cursor: 'pointer', color: '#90a4ae', fontSize: '0.8rem' }} onClick={() => setSearch('')} onKeyDown={(e) => e.key === 'Enter' && setSearch('')}>✕</span>}
          </div>
          <select className="page__filter-select" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
            <option value="all">{t('decisions.allBranches')}</option>
            {branches.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loadError && (
          <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>
        )}
        {loading ? (
          <div className="list-page-card__loading">{t('common.loading')}</div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div className="table-card">
                <div className="empty-state">
                  <div className="empty-state__icon"><Icon name="decisions" size={48} color="#cfd8dc" strokeWidth={1.2} /></div>
                  <div className="empty-state__title">{t('decisions.noMatch')}</div>
                  <div className="empty-state__sub">{t('decisions.noMatchSub')}</div>
                </div>
              </div>
            ) : (
              <div className="mdecisions-grid">
                {filtered.map((d, i) => {
                  const s = STATUS_LABELS[d.status] || STATUS_LABELS.pending;
                  const dl = daysLeft(d.deadline);
                  const urgent = dl !== null && dl >= 0 && dl <= 3 && !d.myVote;
                  return (
                    <div
                      key={d.id}
                      className={`mdecision-card${urgent ? ' mdecision-card--urgent' : ''}${d.myVote ? ` mdecision-card--${d.myVote}` : ''}`}
                      style={{ animationDelay: `${i * 0.055}s` }}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/member/decisions/${d.id}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/member/decisions/${d.id}`)}
                    >
                      {urgent && <div className="mdecision-card__ribbon">{t('decisions.urgent')}</div>}

                      <div className="mdecision-card__top">
                        <div className="mdecision-card__branch">
                          <Icon name="branch" size={12} color="#78909c" />
                          {d.branch}
                        </div>
                        <span className={`status-badge ${s.cls}`}>
                          <Icon name={s.iconName} size={11} /> {s.label}
                        </span>
                      </div>

                      <div className="mdecision-card__title">{d.title}</div>
                      <div className="mdecision-card__desc">{d.description}</div>

                      <div className="mdecision-card__meta">
                        <span className="mdecision-card__meta-item"><Icon name="calendar" size={11} />{d.date}</span>
                        <span className="mdecision-card__meta-item">
                          <Icon name="clock" size={11} color={dl !== null && dl <= 2 ? '#e53935' : '#f57c00'} />
                          <span style={{ color: dl !== null && dl <= 2 && dl >= 0 && !d.myVote ? '#e53935' : '#90a4ae' }}>
                            {dl === null ? '—' : dl >= 0 ? `${dl} ${t('decisions.days')}` : t('decisions.expired')}
                          </span>
                        </span>
                        <span className="mdecision-card__meta-item">
                          <Icon name="user" size={11} />
                          {d.votes.length}/{d.totalMembers} {t('decisions.votedCount')}
                        </span>
                      </div>

                      <div className="mdecision-card__footer">
                        {d.myVote ? (
                          <div className="mdecision-card__my-vote">
                            <Icon name="check-circle" size={13} color="#02c3a6" />
                            <span>{t('decisions.votedByMe')}</span>
                            <span className={`status-badge ${voteLabels[d.myVote].cls}`} style={{ fontSize: '0.68rem', padding: '2px 8px' }}>
                              <Icon name={voteLabels[d.myVote].iconName} size={10} /> {voteLabels[d.myVote].label}
                            </span>
                          </div>
                        ) : (
                          <div className="mdecision-card__cta">
                            <Icon name="voting" size={13} color="#02c3a6" />
                            <span>{t('decisions.clickToVote')}</span>
                          </div>
                        )}
                        <Icon name="chevron-left" size={14} color="#90a4ae" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
